import React, { useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { User, Heart, AlertCircle, TrendingDown, FileText, Stethoscope, Pill, Activity, TrendingUp } from 'lucide-react';
import { documentApi } from '../../services/api';
import { fromReviewCitation } from '../../types/citation';
import type { ReviewCitation } from '../../types/citation';
import { EditableNarrativeSection } from './EditableNarrativeSection';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';

type Citation = ReviewCitation;

interface ClaimantBackgroundSection {
  sectionId: string;
  title: string;
  narrative: string;
  citations?: Citation[];
}

interface MetadataExtractionResponse {
  claimantBackground?: ClaimantBackgroundSection[];
  [key: string]: any;
}

interface ClaimantBackgroundTabProps {
  caseId: string;
}

const SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  demographics: User,
  pre_incident: Heart,
  medical_history: Stethoscope,
  index_event: AlertCircle,
  treatment: Pill,
  post_incident: TrendingDown,
  current_status: Activity,
  prognosis: TrendingUp,
};

const SECTION_GRADIENTS: Record<string, string> = {
  demographics: 'from-cyan-50 to-indigo-50 dark:from-cyan-900/20 dark:to-indigo-900/20',
  pre_incident: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
  medical_history: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
  index_event: 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20',
  treatment: 'from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20',
  post_incident: 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20',
  current_status: 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20',
  prognosis: 'from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20',
};

const SECTION_BORDERS: Record<string, string> = {
  demographics: 'border-cyan-200 dark:border-cyan-800',
  pre_incident: 'border-green-200 dark:border-green-800',
  medical_history: 'border-green-200 dark:border-green-800',
  index_event: 'border-red-200 dark:border-red-800',
  treatment: 'border-purple-200 dark:border-purple-800',
  post_incident: 'border-amber-200 dark:border-amber-800',
  current_status: 'border-amber-200 dark:border-amber-800',
  prognosis: 'border-teal-200 dark:border-teal-800',
};

const SECTION_ICON_COLORS: Record<string, string> = {
  demographics: 'text-cyan-600 dark:text-cyan-400',
  pre_incident: 'text-green-600 dark:text-green-400',
  medical_history: 'text-green-600 dark:text-green-400',
  index_event: 'text-red-600 dark:text-red-400',
  treatment: 'text-purple-600 dark:text-purple-400',
  post_incident: 'text-amber-600 dark:text-amber-400',
  current_status: 'text-amber-600 dark:text-amber-400',
  prognosis: 'text-teal-600 dark:text-teal-400',
};

export const ClaimantBackgroundTab: React.FC<ClaimantBackgroundTabProps> = ({ caseId }) => {
  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false }
  );

  const { data: documentsData } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { refetchOnMount: 'always', refetchOnWindowFocus: true }
  );

  const documents = documentsData || [];
  const OVERVIEW_SECTIONS = ['index_event', 'treatment', 'current_status', 'prognosis'];
  const sections = (metadata?.claimantBackground || []).filter(s => !OVERVIEW_SECTIONS.includes(s.sectionId));
  const { isEventMarked } = useKeyFindings();

  // Auto-scroll to a highlighted section from Key Findings jump
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || !highlightId.startsWith('section-')) return;
    scrollToHighlighted();
  }, [highlightId, scrollToHighlighted]);

  const buildUpdatedMetadata = useCallback((sectionId: string, newNarrative: string) => {
    if (!metadata) return metadata;
    return {
      ...metadata,
      claimantBackground: (metadata.claimantBackground || []).map(s =>
        s.sectionId === sectionId ? { ...s, narrative: newNarrative } : s
      ),
    };
  }, [metadata]);

  if (sections.length === 0) {
    return (
      <div className="p-6 h-full overflow-auto">
        <div className="text-center py-16">
          <User className="w-12 h-12 text-gray-300 dark:text-medical-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-medical-100 mb-2">
            No Claimant Background Available
          </h3>
          <p className="text-sm text-gray-600 dark:text-medical-400 max-w-md mx-auto">
            Claimant background data will appear here after running the bundle analysis.
            Upload documents and run analysis from the Evidence tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto space-y-6">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">
          Claimant Background
        </h3>
        <p className="text-sm text-gray-600 dark:text-medical-400">
          Forensic-detail background of the claimant, synthesised from all case documents.
        </p>
      </div>

      {sections.map((section, idx) => {
        const Icon = SECTION_ICONS[section.sectionId] || FileText;
        const gradient = SECTION_GRADIENTS[section.sectionId] || SECTION_GRADIENTS.demographics;
        const defaultBorder = SECTION_BORDERS[section.sectionId] || SECTION_BORDERS.demographics;
        const iconColor = SECTION_ICON_COLORS[section.sectionId] || 'text-gray-600 dark:text-gray-400';
        const sectionMarked = isEventMarked(`section-${section.sectionId}`);

        return (
          <div
            key={`${section.sectionId}-${idx}`}
            data-event-id={`section-${section.sectionId}`}
            className={`bg-gradient-to-r ${gradient} border rounded-lg p-6 transition-colors duration-150 ${
              sectionMarked ? 'border-amber-300 dark:border-amber-600 shadow-sm' : defaultBorder
            }`}
          >
            <EditableNarrativeSection
              caseId={caseId}
              sectionKey={section.sectionId}
              narrative={section.narrative}
              citations={section.citations?.map(c => {
                const doc = documents.find((d: any) => d.documentId === c.documentId);
                return fromReviewCitation(c, doc?.filename);
              }) || []}
              buildUpdatedMetadata={(newNarrative) => buildUpdatedMetadata(section.sectionId, newNarrative)}
              icon={<Icon className={`w-5 h-5 ${iconColor}`} />}
              title={section.title}
              sourceLabel={`Claimant — ${section.title}`}
            />
          </div>
        );
      })}
    </div>
  );
};
